import { delayedCall, dispatcher, ingenuity, Container, killDelayedCall, SpineBase } from "@quest/base-core";
import {
  BigWinControllerFg,
  SlotAnimationConstant,
  SlotConstantsBehavior,
  SlotEventConstantsBehavior,
  SoundConstant,
} from "@quest/murka-games-behavior-panther/dist";
import { eventDispatchers } from "../Constants/EventDispatchers";
import { SlotEventConstants } from "@quest/slot-core";
import { Linear } from "@quest/pixi-bridge";
import { constants } from "@quest/base-core/dist/detectDeviceEnvironment/Constants";

export class BigWinControllerFgGame extends BigWinControllerFg {
  private fgBigWinBgDesktop: SpineBase;
  private fgBigWinBgPortrait: SpineBase;

  private fgLoopSpineContainer: Container;
  private fgLoopSpinePortraitContainer: Container;

  // Overrided to initialize game end elements
  protected initializeFSBigWinElements(): void {
    super.initializeFSBigWinElements();
    this.fgLoopSpineContainer = this.view.getContainerByID("fgLoopSpineContainer");
    this.fgBigWinBgDesktop = this.fgLoopSpineContainer.children[0];
    this.fgLoopSpine = this.fgLoopSpineContainer.children[1];
  }

  protected initializePortraitComponent(): void {
    this.fgLoopSpinePortraitContainer = this.view.getContainerByID("fgSpinePortraitContainer");
    this.fgBigWinBgPortrait = this.fgLoopSpinePortraitContainer.children[0];
    this.fgPortraitLoopSpine = this.fgLoopSpinePortraitContainer.children[1];
  }

  // function to stop big win tickup on stage click
  stopBigWinAndTickerFg() {
    this.freeSpinBigWinPresentationComplete();
    this.freeSpinBigWinMeter.stopTick(false, true, true);
    this.freeSpinBigWinMeter.setCurrencyFormattedValue(String(this.currentWinAmountTickUpFg));
  }
  
  protected subscribeEvents(): void {
    super.subscribeEvents();
    dispatcher.on(eventDispatchers.HIDE_FREEGAME_BIG_WIN, this.stopBigWinAndTickerFg, this);
  }

  // Add to play desktop free spin big win end animation
  protected stopFgSpineInLoop(): void {
    this.fgLoopSpine.playAnim(SlotAnimationConstant.FG_BIG_WIN_DESKTOP_END, false);
    this.fgBigWinBgDesktop.playAnim("out_desktop_bg", false);
    this.fgLoopSpine.onComplete.addOnce(() => {
      this.fgLoopSpine.visible = false;
      this.fgLoopSpine.setPause(true);
      this.fgLoopSpine.onComplete.dispose();
      this.fgBigWinBgDesktop.visible = false;
    }, this);
  }

  // Add to play portrait free spin big win end animation
  protected stopFgPortraitSpineInLoop(): void {
    this.fgPortraitLoopSpine.playAnim(SlotAnimationConstant.FG_BIG_WIN_PORTRAIT_END, false);
    this.fgBigWinBgPortrait.playAnim("out_portrait_fg", false);
    this.fgPortraitLoopSpine.onComplete.addOnce(() => {
      this.fgPortraitLoopSpine.visible = false;
      this.fgPortraitLoopSpine.setPause(true);
      this.fgPortraitLoopSpine.onComplete.dispose();
      this.fgBigWinBgPortrait.visible = false;
    }, this);
  }

  /**
   * override to stop meter tweening ans set meter postion as per orientation
   */
  protected showFSBigWinTickup(): void {
    this.freeSpinBigWinContainer.visible = true;
    this.setWinMtrPosition();
    const moveTween = ingenuity.currentGame.add
      .tween(this.freeSpinBigWinContainer)
      .to(
        { y: this.freeSpinBigWinContainer.json.finalY },
        SlotConstantsBehavior.TICKUP_MOVE_IN_LEFT_DURATION,
        Linear.None,
        true,
        0,
        0,
        false
      );
    moveTween.onComplete.addOnce(() => {
      this.startFSBigWinTickup();
      moveTween.stop();
      ingenuity.currentGame.tweens.remove(moveTween);
    }, this);
  }

  /**
   * set mtr position according to orientation
   */
  private setWinMtrPosition(): void {
    if(ingenuity.deviceEnv.isDesktop){
      this.freeSpinBigWinContainer.x = 180;
    } else{
      if (ingenuity.deviceEnv.getOrientation() === constants.ORIENTATION.PORTRAIT) {
        this.freeSpinBigWinContainer.x = 280;
      } else {
        this.freeSpinBigWinContainer.x = 200;
      }
      this.freeSpinBigWinContainer.y = this.freeSpinBigWinContainer.json.initialY;
    }
  }

  /**
   * overide to play hide portrait in intro screen
   */
  protected playFgSpineInLoop(): void {
    this.fgLoopSpine.setPause(false);
    this.fgLoopSpine.visible = true;
    this.fgLoopSpine.playAnim(SlotAnimationConstant.FG_BIG_WIN_DESKTOP_START, false);
    this.fgBigWinBgDesktop.visible = true;
    this.fgBigWinBgDesktop.playAnim("In_desktop_bg", false);
    this.showFSBigWinTickup();
    this.fgLoopSpine.onComplete.addOnce(() => {
      this.fgLoopSpine.playAnim(SlotAnimationConstant.FG_BIG_WIN_DESKTOP_LOOP, true);
      this.fgBigWinBgDesktop.playAnim("Loop_desktop_bg", true);
    }, this);
  }

  // Overrided to play portrait spine animation
  protected playFgPortraitSpineInLoop(): void {
    this.fgPortraitLoopSpine.visible = true;
    this.fgPortraitLoopSpine.setPause(false);
    this.fgPortraitLoopSpine.playAnim(SlotAnimationConstant.FG_BIG_WIN_PORTRAIT_START, false);
    this.fgBigWinBgPortrait.visible = true;
    this.fgBigWinBgPortrait.playAnim("In_portrait_bg", false);
    this.fgPortraitLoopSpine.onComplete.addOnce(() => {
      this.fgPortraitLoopSpine.playAnim(SlotAnimationConstant.FG_BIG_WIN_PORTRAIT_LOOP, true);
      this.fgBigWinBgPortrait.playAnim("Loop_portrait_bg", true);
    }, this);
  }

  /**
   * overide to tween big win amout to left when banner is out
   */
  protected moveFSBigWinMeterInLeft(): void {
    delayedCall("startMtrTween", 560, () => {
      killDelayedCall("startMtrTween");
      const moveInLeftTween = ingenuity.currentGame.add
        .tween(this.freeSpinBigWinContainer)
        .to(
          { x: this.freeSpinBigWinContainer.json.finalLeftX, y: this.freeSpinBigWinContainer.json.finalLeftY },
          SlotConstantsBehavior.DURATION_100,
          Linear.None,
          true,
          0,
          0,
          false
        );
      moveInLeftTween.onComplete.addOnce(() => {
        moveInLeftTween.stop();
        ingenuity.currentGame.tweens.remove(moveInLeftTween);
        this.freeSpinBigWinHide();
        this.resetFSBigWinElements();
        this.stopFSBigWinAndTicker();
      }, this);
    });
  }

  /**
   * function to handle on stage click
   */
  protected onForcestopFSBigWinAndTicker(): void {
    this.freeSpinBigWinMeter.stopTick(false, true, true);
    this.freeSpinBigWinMeter.setCurrencyFormattedValue(String(this.currentWinAmountTickUpFg));
    this.freeSpinBigWinPresentationComplete();
  }

  protected stopFSBigWinAndTicker(): void {
    this.freeSpinBigWinMeter.stopTick(false, true, true);
    this.freeSpinBigWinMeter.setCurrencyFormattedValue(String(this.currentWinAmountTickUpFg));
  }

  protected startFSBigWin(): void {
    dispatcher.fireEvent(SlotEventConstantsBehavior.FADE_IN_SOUND, {
      id: SoundConstant.FREGAME_BGLOOP,
      vol: 0.3,
      key: SlotConstantsBehavior.SOUND_LIST_POSTLOAD,
    });
    //To add bottom bar on top
    dispatcher.fireEvent(SlotEventConstantsBehavior.ADD_BOTTOM_BAR_ON_TOP, this);
    delayedCall(
      SlotConstantsBehavior.HIDE_FREE_GAME_WIN_METER_DELAY,
      SlotConstantsBehavior.DURATION_1000,
      () => {
        killDelayedCall(SlotConstantsBehavior.HIDE_FREE_GAME_WIN_METER_DELAY);
        dispatcher.fireEvent(SlotEventConstantsBehavior.HIDE_FREE_GAME_WIN_METER);
      },
      this
    );
    this.model.setIsBigWinRunning(true);
    dispatcher.fireEvent(SlotEventConstants.FG_DISABLED_ALL_BUTTONS);
    this.freeSpinBigWinMeter.visible = false;
    this.freeSpinBigWinMeter.setCurrencyFormattedValue(0);
    this.playFgSpineInLoop();
    !ingenuity.deviceEnv.isDesktop && this.playFgPortraitSpineInLoop();
    dispatcher.fireEvent(SlotEventConstantsBehavior.ADD_FG_BUTTON_PANEL_ON_TOP);
    this.addFSBigWinOnTop();
    this.createMask();
  }
}
